"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";

interface MultiUserBadgeProps {
  item: unknown;
  disabled?: boolean;
}

function MultiUserBadge({ item, disabled }: MultiUserBadgeProps) {
  if (typeof item === "object" && item !== null && "id" in (item as Record<string, unknown>)) {
    const userData = item as { id: string } & Record<string, unknown>;
    return (
      <SmartUserDisplay
        userId={userData.id}
        userData={userData}
        showIcon={true}
        className="inline-block"
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  return (
    <SmartUserDisplay
      userId={String(item)}
      showIcon={true}
      className="inline-block"
      disabled={disabled}
      showPopover={false}
    />
  );
}

interface MultiUserDisplayProps {
  value: unknown;
  disabled: boolean;
}

/** Inline multi-user display (read mode). Caps at 3 with overflow badge. */
export function MultiUserDisplay({ value, disabled }: MultiUserDisplayProps) {
  if (Array.isArray(value)) {
    return (
      <div className="flex gap-1 flex-wrap">
        {value.slice(0, 3).map((userItem, index) => (
          <MultiUserBadge key={index} item={userItem} disabled={disabled} />
        ))}
        {value.length > 3 && (
          <Badge variant="secondary" className="text-xs">
            +{value.length - 3}
          </Badge>
        )}
      </div>
    );
  }
  return (
    <SmartUserDisplay
      userId={String(value)}
      showIcon={true}
      className="inline-block"
      disabled={disabled}
      showPopover={false}
    />
  );
}

/** Selected-users panel shown above the multi-user edit input. */
export function MultiUserCurrentSelections({ value }: { value: unknown }) {
  if (!Array.isArray(value) || value.length === 0) return null;
  return (
    <div className="p-3 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Currently Selected:</div>
      <div className="flex gap-1 flex-wrap">
        {value.map((userItem, index) => (
          <MultiUserBadge key={index} item={userItem} />
        ))}
      </div>
    </div>
  );
}
